import { useState } from 'react'
import { Flag, AlertTriangle, Clock, TrendingUp } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DJENPublication, PublicationPriority } from '@/types/publication'
import { cn } from '@/utils/cn'

interface SetPriorityModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (priority: PublicationPriority, reason?: string) => void
  publication: DJENPublication | null
  loading?: boolean
}

interface PriorityConfig {
  value: PublicationPriority
  label: string
  description: string
  /** Classes de tom (tokens do tema) do ícone e do selo da prioridade. */
  tone: string
  icon: React.ReactNode
  suggestedReasons: string[]
}

const PRIORITY_CONFIGS: PriorityConfig[] = [
  {
    value: 'LOW',
    label: 'Baixa',
    description: 'Publicação sem urgência, pode ser tratada conforme demanda regular',
    tone: 'bg-muted text-muted-foreground',
    icon: <Flag className="size-4" />,
    suggestedReasons: [
      'Sem prazo específico',
      'Comunicação administrativa',
      'Baixo impacto para o cliente',
      'Pode aguardar outros processos'
    ]
  },
  {
    value: 'MEDIUM',
    label: 'Média',
    description: 'Prioridade padrão para a maioria das publicações',
    tone: 'bg-secondary text-secondary-foreground',
    icon: <Clock className="size-4" />,
    suggestedReasons: [
      'Prazo padrão do processo',
      'Importância média para o cliente',
      'Fluxo normal de trabalho',
      'Sem urgência especial'
    ]
  },
  {
    value: 'HIGH',
    label: 'Alta',
    description: 'Publicação importante que requer atenção prioritária',
    tone: 'border border-border bg-background text-foreground',
    icon: <TrendingUp className="size-4" />,
    suggestedReasons: [
      'Prazo próximo do vencimento',
      'Alto valor para o cliente',
      'Processo estratégico',
      'Risco de perda de prazo'
    ]
  },
  {
    value: 'URGENT',
    label: 'Urgente',
    description: 'Requer ação imediata - prazo crítico ou alta importância',
    tone: 'bg-destructive/10 text-destructive',
    icon: <AlertTriangle className="size-4" />,
    suggestedReasons: [
      'Prazo vencendo hoje/amanhã',
      'Intimação judicial urgente',
      'Processo de grande valor',
      'Risco de preclusão'
    ]
  }
]

export function SetPriorityModal({
  isOpen,
  onClose,
  onConfirm,
  publication,
  loading = false
}: SetPriorityModalProps) {
  const [selectedPriority, setSelectedPriority] = useState<PublicationPriority>('MEDIUM')
  const [reason, setReason] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onConfirm(selectedPriority, reason || undefined)
    resetForm()
  }

  const resetForm = () => {
    setSelectedPriority('MEDIUM')
    setReason('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const selectedConfig = PRIORITY_CONFIGS.find(config => config.value === selectedPriority)!
  const currentConfig = publication ? PRIORITY_CONFIGS.find(config => config.value === publication.priority) : undefined

  // Análise automática de urgência baseada na publicação
  const getUrgencyAnalysis = () => {
    if (!publication) return null

    const analysisPoints: string[] = []
    let suggestedPriority: PublicationPriority = 'MEDIUM'

    // Análise do tipo de comunicação
    const urgentTypes = ['Intimação', 'Citação', 'Sentença']
    if (urgentTypes.includes(publication.tipoComunicacao)) {
      analysisPoints.push(`Tipo "${publication.tipoComunicacao}" geralmente requer atenção prioritária`)
      suggestedPriority = 'HIGH'
    }

    // Análise do tribunal
    const highProfileCourts = ['STF', 'STJ', 'TST']
    if (highProfileCourts.includes(publication.siglaTribunal)) {
      analysisPoints.push(`Tribunal superior (${publication.siglaTribunal}) - alta importância`)
      suggestedPriority = 'HIGH'
    }

    // Análise de data (se recente, pode ser mais urgente)
    const publicationDate = new Date(publication.dataDisponibilizacao)
    const today = new Date()
    const daysDiff = Math.floor((today.getTime() - publicationDate.getTime()) / (1000 * 60 * 60 * 24))
    
    if (daysDiff <= 1) {
      analysisPoints.push('Publicação muito recente - pode ter prazos curtos')
      if (suggestedPriority !== 'HIGH') suggestedPriority = 'MEDIUM'
    }

    // Análise de palavras-chave no texto
    const urgentKeywords = ['urgente', 'prazo', 'vencimento', 'improrrogável', 'intimação']
    const hasUrgentKeywords = urgentKeywords.some(keyword => 
      publication.texto.toLowerCase().includes(keyword)
    )
    
    if (hasUrgentKeywords) {
      analysisPoints.push('Texto contém palavras que indicam urgência')
      suggestedPriority = 'HIGH'
    }

    return {
      points: analysisPoints,
      suggestedPriority,
      confidence: analysisPoints.length
    }
  }

  const urgencyAnalysis = getUrgencyAnalysis()

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto max-sm:max-h-dvh">
        <DialogHeader>
          <DialogTitle>Definir Prioridade da Publicação</DialogTitle>
        </DialogHeader>

        {publication && (
          <div className="rounded-lg border bg-muted/40 p-3">
            <h4 className="mb-2 font-medium">Publicação</h4>
            <div className="flex flex-col gap-1 break-words text-sm text-muted-foreground">
              <p><strong>Tipo:</strong> {publication.tipoComunicacao}</p>
              <p><strong>Processo:</strong> {publication.numeroProcessoComMascara}</p>
              <p><strong>Tribunal:</strong> {publication.siglaTribunal} - {publication.nomeOrgao}</p>
              <p className="flex flex-wrap items-center gap-2">
                <strong>Prioridade Atual:</strong>
                {currentConfig && (
                  <span className={cn('inline-flex items-center rounded-4xl px-2 py-0.5 text-xs font-medium', currentConfig.tone)}>
                    {currentConfig.label}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Análise Automática de Urgência */}
        {urgencyAnalysis && urgencyAnalysis.points.length > 0 && (
          <div role="note" className="rounded-lg border border-l-4 border-l-primary p-3">
            <h4 className="mb-2 flex items-center gap-2 font-medium">
              <AlertTriangle className="size-4 text-primary" aria-hidden="true" />
              Análise Automática de Urgência
            </h4>
            <ul className="mb-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {urgencyAnalysis.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">
                Prioridade Sugerida: {PRIORITY_CONFIGS.find(p => p.value === urgencyAnalysis.suggestedPriority)?.label}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedPriority(urgencyAnalysis.suggestedPriority)}
              >
                Aplicar Sugestão
              </Button>
            </div>
          </div>
        )}

        <form id="set-priority-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Seleção de Prioridade */}
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-medium">Selecionar Nova Prioridade</legend>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {PRIORITY_CONFIGS.map((config) => {
                const selected = selectedPriority === config.value
                return (
                  <button
                    key={config.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedPriority(config.value)}
                    className={cn(
                      'rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span className={cn('inline-flex size-8 shrink-0 items-center justify-center rounded-full', config.tone)}>
                        {config.icon}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium">{config.label}</h4>
                        <p className="mt-1 text-sm text-muted-foreground">{config.description}</p>
                      </div>
                      {selected && <Flag className="size-5 shrink-0 text-primary" aria-hidden="true" />}
                    </div>
                  </button>
                )
              })}
            </div>
          </fieldset>

          {/* Motivo da Alteração */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="set-priority-reason">Motivo da Priorização</Label>
            <Textarea
              id="set-priority-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="resize-none"
              rows={3}
              placeholder="Explique o motivo para esta prioridade..."
            />

            {/* Sugestões de Motivos */}
            <div className="mt-2">
              <p className="mb-2 text-sm text-muted-foreground">Sugestões para {selectedConfig.label}:</p>
              <div className="flex flex-wrap gap-2">
                {selectedConfig.suggestedReasons.map((suggestion) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="secondary"
                    size="xs"
                    onClick={() => setReason(suggestion)}
                    className="h-auto whitespace-normal rounded-full py-1 text-left"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Resumo da Alteração */}
          <div className="rounded-lg border bg-muted/40 p-3">
            <h4 className="mb-2 flex items-center gap-2 font-medium">
              <span className={cn('inline-flex size-6 items-center justify-center rounded-full', selectedConfig.tone)}>
                {selectedConfig.icon}
              </span>
              Resumo da Priorização
            </h4>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p><strong>Nova Prioridade:</strong> {selectedConfig.label}</p>
              <p><strong>Descrição:</strong> {selectedConfig.description}</p>
              {publication && (
                <p><strong>Alteração:</strong> {currentConfig?.label} → {selectedConfig.label}</p>
              )}
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" form="set-priority-form" disabled={loading}>
            {loading ? 'Definindo...' : 'Definir Prioridade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
